import { useEffect, useRef, useState, type FormEvent } from "react";
import { ImagePlus, Send, X } from "lucide-react";

import { Button, useConfirm } from "@/ui";
import { useBroadcastToBot } from "@/features/queries";

const TEXT_LIMIT = 4096;
const CAPTION_LIMIT = 1024;

export function AdminBroadcastPage() {
  const broadcast = useBroadcastToBot();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ queued: number } | null>(null);

  useEffect(() => {
    if (!image) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const limit = image ? CAPTION_LIMIT : TEXT_LIMIT;
  const overLimit = text.length > limit;
  const canSubmit = (text.trim().length > 0 || image !== null) && !overLimit && !broadcast.isPending;

  function pickImage(f: File | null) {
    if (!f) return setImage(null);
    if (!/^image\//.test(f.type)) {
      alert("Можно загрузить только картинку (JPEG/PNG/WEBP/GIF).");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      alert("Картинка больше 10 МБ.");
      return;
    }
    setImage(f);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const ok = await confirm({
      title: "Разослать всем?",
      description:
        "Сообщение уйдёт каждому зарегистрированному пользователю Telegram-бота. Отменить рассылку нельзя.",
      confirmLabel: "Разослать",
    });
    if (!ok) return;

    try {
      const result = await broadcast.mutateAsync({ text: text.trim(), image });
      setLastResult({ queued: result.queued });
      setText("");
      setImage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      // Error rendered below from broadcast.error.
    }
  }

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 className="h1" style={{ margin: 0 }}>Рассылка в бот</h1>
        <p className="caption" style={{ color: "var(--fg2)", marginTop: 4 }}>
          Сообщение получит каждый зарегистрированный пользователь. Поддерживается
          HTML-разметка Telegram (<code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;a&gt;</code>).
          С картинкой текст идёт подписью — лимит {CAPTION_LIMIT} символов.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        style={{
          background: "var(--bg-section)",
          borderRadius: "var(--r-lg)",
          padding: 24,
          maxWidth: 640,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="label" style={{ color: "var(--fg2)" }}>Текст сообщения</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            placeholder="Привет! У нас новости…"
            style={{
              border: "1px solid var(--separator)",
              borderRadius: "var(--r-md)",
              padding: "10px 14px",
              fontFamily: "var(--font-body)",
              fontSize: 15,
              background: "var(--bg)",
              color: "var(--fg1)",
              outline: "none",
              width: "100%",
              boxSizing: "border-box",
              resize: "vertical",
            }}
          />
          <div
            className="caption"
            style={{
              color: overLimit ? "var(--fg-danger)" : "var(--fg3)",
              alignSelf: "flex-end",
            }}
          >
            {text.length} / {limit}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="label" style={{ color: "var(--fg2)" }}>Картинка (необязательно)</span>
          {previewUrl ? (
            <div
              style={{
                position: "relative",
                borderRadius: "var(--r-md)",
                overflow: "hidden",
                background: "var(--bg)",
                border: "1px solid var(--separator)",
              }}
            >
              <img
                src={previewUrl}
                alt="Превью"
                style={{ display: "block", width: "100%", maxHeight: 320, objectFit: "contain" }}
              />
              <button
                type="button"
                onClick={() => {
                  setImage(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  border: 0,
                  borderRadius: 999,
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Убрать картинку"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label
              style={{
                border: "1px dashed var(--separator)",
                borderRadius: "var(--r-md)",
                padding: "20px 14px",
                color: "var(--fg2)",
                fontSize: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                justifyContent: "center",
              }}
            >
              <ImagePlus size={18} />
              <span>Выбрать файл (JPEG, PNG, WEBP, GIF, до 10&nbsp;МБ)</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
                style={{ display: "none" }}
              />
            </label>
          )}
        </div>

        {broadcast.error && (
          <div
            style={{
              background: "rgba(223,63,64,0.1)",
              color: "var(--fg-danger)",
              padding: "8px 12px",
              borderRadius: "var(--r-md)",
              fontSize: 13,
            }}
          >
            {(broadcast.error as Error).message}
          </div>
        )}

        {lastResult && !broadcast.isPending && !broadcast.error && (
          <div
            style={{
              background: "rgba(36,138,61,0.12)",
              color: "var(--fg-success, #248a3d)",
              padding: "8px 12px",
              borderRadius: "var(--r-md)",
              fontSize: 13,
            }}
          >
            Рассылка поставлена в очередь: {lastResult.queued} получателей.
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button type="submit" disabled={!canSubmit}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Send size={14} />
              {broadcast.isPending ? "Отправляем…" : "Разослать"}
            </span>
          </Button>
        </div>
      </form>
    </div>
  );
}
